import {api} from "@/shared/api";

import {DialogMode} from "@/shared/recording/recordDialog";
import {WorkingTimeRequestRevisionsTable} from "@/features/requests/workingTimeRequestRevisionsTable";
import {SubstituteSelection} from "@/shared/workingtime/substituteSelection";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {Combobox} from "@/shared/ui/combobox";
import {SubstituteValidation} from "@/shared/workingtime/substituteValidation";
import {SaveWorkingTimeRequestResponse, WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {WorkingTimeRequestStepsComponent} from "./workingTimeRequestStepsComponent";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {permissionForSaveAndApproveButton} from "@/shared/securityHelper";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	AbstractWorkingTimeRequestResponseModel,
	CalculateVacationResponse,
	CustomFieldDefinition,
	CustomFieldOption,
	ErrorResponse,
	FieldError,
	FilterUsersWithTaskLeaderResponse,
	PartialUser,
	PartialWorkingTimeType,
	User,
	WorkingTimeType
} from "@/shared/timr";
import {
	formatVacation,
	getCurrentISOOffset,
	getISODate,
	getISOZonedDateTime,
	parseISODate
} from "@/shared/utils/datefunctions";
import {
	DurationUnit,
	ProofRequirement,
	WorkingTimeRequestStatus,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";
import {ProofAttachmentGallery} from "@/shared/workingtime/proofAttachmentGallery";
import {initCustomFields, removeCustomFieldValues, updateCustomFields} from "@/shared/customFieldHelper";
import {WorkingTimeRangePanelBridge, type WorkingTimeRangeValue} from "@/shared/ui/workingTimeRange/WorkingTimeRangePanelBridge";
import {parseRequestEndpoint} from "@/shared/ui/workingTimeRange/workingTimeRangeState";

enum WorkingTimeRequestDialogMode { Edit = "Edit", Show = "Show", Duplicate = "Duplicate" }

export interface WorkingTimeRequestDialogConfig {
	workingTimeTypes: WorkingTimeType[];
	dialogId: string;
	isGroupLeaderForThemself: boolean;
	userSelectionEnabled?: boolean;
	title?: {
		[key in keyof typeof DialogMode]: string
	};
	size?: BootstrapModal.ModalSize;
	customFieldDefinitions?: CustomFieldDefinition[];
}

export class WorkingTimeRequestDialog {

	public static EVENT_REQUEST_EDITED = "workingTimeRequestEdited";
	public static EVENT_REQUEST_DELETED = "workingTimeRequestDeleted";
	public static EVENT_REQUEST_STATUS_CHANGED = "workingTimeRequestStatusChanged";

	protected dialog: BootstrapModal.Modal;
	protected dialogMode: WorkingTimeRequestDialogMode;
	protected dom: JQuery;
	private updateVacationInformationRequest: JQuery.jqXHR;
	private editUnit: DurationUnit;
	private customFieldComboboxes: Combobox<CustomFieldOption>[] = [];
	private hasCustomFieldCheckboxChangeListener: boolean[] = [];
	private workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
	private workingTimeRequestStatus: WorkingTimeRequestStatus;
	private workingTimeRequestRevisionsTable: WorkingTimeRequestRevisionsTable;
	private descriptionTypeahead: DescriptionTypeahead;
	private rangePanel: WorkingTimeRangePanelBridge;
	private rangeValue: WorkingTimeRangeValue;
	private rangeFieldsDisabled = false;
	private userCombobox: Combobox<PartialUser>;
	private substituteSelection?: SubstituteSelection;
	private substituteValidation?: SubstituteValidation;
	private readonly approvalWorkingTimeRequestStepsComponent: WorkingTimeRequestStepsComponent;
	private readonly cancellationWorkingTimeRequestStepsComponent: WorkingTimeRequestStepsComponent;
	private readonly proofAttachmentGallery: ProofAttachmentGallery;

	private currentRequestCount = 0;

	constructor(public config: WorkingTimeRequestDialogConfig) {
		this.config = config;
		this.config.size = this.config.size || "modal-xl";

		this.dom = $(this.config.dialogId);
		this.init();

		this.initChangeListeners();
		this.initWorkingTimeRequestRevisionsTable();
		this.initWorkingTimeTypeSelect();
		this.initUserCombobox();
		this.initSubstitutes();
		if (SecurityStatus.isCustomFieldsForRecordAllowed()) {
			initCustomFields(this.dom, this.config.customFieldDefinitions, this.customFieldComboboxes, this.hasCustomFieldCheckboxChangeListener);
		}

		this.approvalWorkingTimeRequestStepsComponent = document.getElementById("workingTimeRequestDialogWorkingTimeRequestStepsApproval") as WorkingTimeRequestStepsComponent;
		this.cancellationWorkingTimeRequestStepsComponent = document.getElementById("workingTimeRequestDialogWorkingTimeRequestStepsCancellation") as WorkingTimeRequestStepsComponent;

		this.proofAttachmentGallery = document.getElementById("workingTimeRequestDialogProofAttachmentGallery") as ProofAttachmentGallery;
		this.proofAttachmentGallery?.init("requests/getProofAttachments.json");
		// THe save button has to appear when attachments are changed, because otherwise the user could add attachments,
		// but then lose them by saving the request without attachments,
		// because admins and possible team leaders can change attachments even if the request is approved
		this.proofAttachmentGallery?.addEventListener(ProofAttachmentGallery.EVENT_FILES_CHANGED, () => {
			// Only show save button if there is a proof associated with the request
			this.dialog.toggleButton(
				"saveProofButton",
				this.workingTimeRequest.proofRequirement !== ProofRequirement.NOT_APPLICABLE &&
				WorkingTimeRequestUtil.isAllowedToChangeProofAttachments(this.workingTimeRequest, this.config.isGroupLeaderForThemself) &&
				!this.isSaveButtonVisible()
			);
		});
	}

	private init() {
		const onImmediateActionSuccess = () => {
			this.onWorkingTimeRequestStatusChanged();
			this.closeDialog();
		};

		this.dialog = new BootstrapModal.RecordModal({
			size: this.config.size,
			title: this.config.title ? this.config.title[this.dialogMode] : undefined,
			message: this.dom,
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: [{
				text: "dialog.delete",
				action: () => {
					this._delete();
				},
				css: "deleteButton btn-outline-danger",
				left: true
			}, {
				text: "confirmation.requestCancellation",
				action: () => this.saveProofsBeforeAction()
					.then(() => this.requestCancellation())
					.catch(() => this.dialog.resetLoadingButtons()),
				css: "requestCancellationButton btn-danger",
				left: true
			}, {
				text: "confirmation.cancelImmediately",
				action: () => this.saveProofsBeforeAction()
					.then(() => WorkingTimeRequestUtil.cancelImmediatelyWorkingTimeRequest(this.workingTimeRequest, onImmediateActionSuccess, () => this.dialog.resetLoadingButtons())),
				css: "cancelImmediatelyButton btn-danger",
				visible: false,
				left: true
			}, {
				text: "dialog.saveAndApprove",
				action: () => this.checkConfigurationAndSave(this.dialog, true),
				css: "saveAndApproveImmediatelyButton btn-primary"
			}, {
				text: "dialog.request",
				action: () => this.checkConfigurationAndSave(this.dialog),
				css: "requestButton btn-primary"
			}, {
				text: "dialog.save",
				action: () => this.checkConfigurationAndSave(this.dialog),
				css: "saveButton btn-primary"
			}, {
				text: "dialog.save",
				action: () => this.saveProofAttachmentsOnly(),
				css: "saveProofButton btn-primary"
			}, {
				text: "dialog.cancel",
				action: () => this.closeDialog(),
				css: "cancelButton btn-default"
			}, {
				text: "requests.workingTimeRequestsUser.context.approveImmediately",
				action: () => this.saveProofsBeforeAction()
					.then(() => WorkingTimeRequestUtil.approveWorkingTimeRequestImmediately(this.workingTimeRequest, onImmediateActionSuccess, () => this.dialog.resetLoadingButtons())),
				left: true,
				css: "approveImmediatelyButton btn-success"
			}, {
				text: "requests.workingTimeRequestsUser.context.rejectImmediately",
				action: () => this.saveProofsBeforeAction()
					.then(() => WorkingTimeRequestUtil.rejectWorkingTimeRequestImmediately(this.workingTimeRequest, onImmediateActionSuccess, () => this.dialog.resetLoadingButtons())),
				left: true,
				css: "rejectImmediatelyButton btn-danger"
			}, {
				text: "requests.workingTimeRequestsUser.context.approveCancellationImmediately",
				action: () => this.saveProofsBeforeAction()
					.then(() => WorkingTimeRequestUtil.approveCancellationWorkingTimeRequestImmediately(this.workingTimeRequest, onImmediateActionSuccess, () => this.dialog.resetLoadingButtons())),
				left: true,
				css: "approveCancellationImmediatelyButton btn-success"
			}, {
				text: "requests.workingTimeRequestsUser.context.rejectCancellationImmediately",
				action: () => this.saveProofsBeforeAction()
					.then(() => WorkingTimeRequestUtil.rejectCancellationWorkingTimeRequestImmediately(this.workingTimeRequest, onImmediateActionSuccess, () => this.dialog.resetLoadingButtons())),
				left: true,
				css: "rejectCancellationImmediatelyButton btn-danger"
			}],
			checkbox: false,
			onOpen: () => {
				this.updateBorder();
				this.initButtonLoadingListeners();
			},
			onClose: () => this.closeDialog()
		});

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.dom.find(".descriptionTypeahead"),
			url: "filterWorkingTimeDescriptions.json"
		});
	}

	private initWorkingTimeRequestRevisionsTable() {
		this.workingTimeRequestRevisionsTable = new WorkingTimeRequestRevisionsTable({
			element: $("#workingTimeRequestRevisionsTable"),
			orderable: false,
			clampNote: true,
			allowExpand: true
		});
		this.dom.find("#workingTimeRequestRevisionTableCollapseToggle").click(() => {
			this.dom.find("#workingTimeRequestRevisionTableCollapse").collapse("toggle");
		});
		this.dom.find("#workingTimeRequestRevisionTableCollapse")
			.on("show.bs.collapse", () => {
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextShow").hide();
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextHide").show();
			})
			.on("hide.bs.collapse", () => {
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextShow").show();
				this.dom.find("#workingTimeRequestRevisionTableCollapseTextHide").hide();
			});
	}

	private initChangeListeners() {
		this.dom.find("#workingTimeRequestDialogWorkingTimeType").on("change", () => {
			this.onSelectedWorkingTimeTypeChanged();
		});
	}

	/**
	 * The timezone-related config only makes sense per shown request (revealed when duplicating), so the
	 * bridge is torn down and recreated whenever a request is (re-)shown instead of taking new props. The
	 * panel picks its day/minute sub-panel from the {@link editUnit} passed on every {@link updateRangePanel}.
	 */
	private initRangePanel(duplicate: boolean) {
		this.rangePanel?.destroy();
		this.rangePanel = new WorkingTimeRangePanelBridge({
			container: document.getElementById("workingTimeRequestDialogRangePanel"),
			startLabel: messages["workingTime.from"],
			endLabel: messages["workingTime.to"],
			breakTimeEnabled: globals.manualBreaksAllowed,
			forceTimeZoneFields: duplicate,
			localizeCalculatedOffset: duplicate,
			onChange: value => this.onRangeChanged(value)
		});
		this.updateRangePanel();
	}
	private onRangeChanged(value: WorkingTimeRangeValue) {
		this.rangeValue = value;
		this.substituteValidation?.setStartEndDate(value.start?.toPlainDate() ?? null, value.end?.toPlainDate() ?? null);
		this.updateVacationInformation();
	}

	private updateRangePanel(fieldErrors: FieldError[] = []) {
		if (!this.rangeValue) {
			return;
		}
		this.rangePanel.update(this.rangeValue, this.editUnit, this.rangeFieldsDisabled, fieldErrors);
	}

	private onWorkingTimeRequestEdited() {
		$(this).triggerHandler(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED);
	}

	private onWorkingTimeRequestDeleted() {
		$(this).triggerHandler(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED);
	}

	private onWorkingTimeRequestStatusChanged() {
		$(this).triggerHandler(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED);
	}

	public show(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.dialogMode = WorkingTimeRequestDialogMode.Show;
		this.showRequest(workingTimeRequest);
		this.updateActionButtonVisibility(workingTimeRequest);
		this.disableFields(true);
	}

	public edit(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.dialogMode = WorkingTimeRequestDialogMode.Edit;
		this.showRequest(workingTimeRequest);
		this.updateActionButtonVisibility(workingTimeRequest);
		this.disableFields(false, ["#workingTimeRequestDialogWorkingTimeType"]);
		this.userCombobox.setDisabled(true);
	}

	public duplicate(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, showUserSelection: boolean = false) {
		this.dialogMode = WorkingTimeRequestDialogMode.Duplicate;
		this.filterAndFillUserCombobox(workingTimeRequest, showUserSelection);
	}

	private filterAndFillUserCombobox(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, showUserSelection: boolean) {
		void api("reports/filterUsers.json")
			.get({
				showAllUsers: true,
				activeUsersOnly: true,
				hideCurrentUserIfGroupLeader: true
			})
			.done((data: FilterUsersWithTaskLeaderResponse) => {
				if (data?.users) {
					this.userCombobox.updateItems(data.users, workingTimeRequest.user);
					this.showRequest(workingTimeRequest, showUserSelection);
					this.updateActionButtonVisibility(workingTimeRequest, showUserSelection);
					this.disableFields(false);
					this.userCombobox.setDisabled(false);
				}
			});
	}

	private closeDialog() {
		this.proofAttachmentGallery?.reset();
		this.dialog.close();
	}

	private showRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, showUserSelection = false) {
		this.workingTimeRequest = workingTimeRequest;
		this.workingTimeRequestStatus = workingTimeRequest.status;
		if (WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest)) {
			// A duplicate is a new request → compute the requirement from the current config (null).
			// Editing/showing an existing request → use its stored requirement.
			this.substituteSelection?.setSubstitute(
				workingTimeRequest.substitute,
				this.dialogMode === WorkingTimeRequestDialogMode.Duplicate ? null : workingTimeRequest.requiresSubstitute
			);
		} else {
			// Only date span requests have substitutes
			this.substituteSelection?.setSubstitute(null, WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest) ? false : null);
		}

		this.dom.find("#workingTimeRequestDialogUserRow").toggle(
			this.dialogMode === WorkingTimeRequestDialogMode.Duplicate
				? showUserSelection
				: !!this.config.userSelectionEnabled
		);

		if (this.dialogMode === WorkingTimeRequestDialogMode.Duplicate) {
			this.proofAttachmentGallery?.reset();
		} else {
			this.proofAttachmentGallery?.loadProofAttachments({workingTimeRequestId: workingTimeRequest.id});
		}
		this.proofAttachmentGallery?.setReadonly(!WorkingTimeRequestUtil.isAllowedToChangeProofAttachments(workingTimeRequest, this.config.isGroupLeaderForThemself));

		if (this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate) {
			this.userCombobox.updateItems([workingTimeRequest.user], workingTimeRequest.user);
		}
		this.workingTimeRequestRevisionsTable.filterByWorkingTimeRequestId(workingTimeRequest.id);

		this.toggleWorkingTimeCollapse();

		if (this.dialogMode === WorkingTimeRequestDialogMode.Duplicate) {
			this.dom.find(".id").val("");
			this.dom.find(".version").val(0);
		} else {
			this.dom.find(".id").val(workingTimeRequest.id);
			this.dom.find(".version").val(workingTimeRequest.version);
		}
		this.dom.find(".userId").val(workingTimeRequest.user.id);
		const startDate = parseISODate(workingTimeRequest.start);
		const endDate = parseISODate(workingTimeRequest.end);
		this.dom.find(".description").val(workingTimeRequest.description);
		this.initWorkingTimeTypeSelect(workingTimeRequest.workingTimeType);
		this.dom.find("#workingTimeRequestDialogWorkingTimeType").val(workingTimeRequest.workingTimeType.id.toString());

		// a new value object re-seeds the panel, discarding edit state from a previously shown request.
		// Has to be done before the init as it is used as the initial state
		this.rangeValue = {
			start: parseRequestEndpoint(workingTimeRequest.start),
			end: parseRequestEndpoint(workingTimeRequest.end),
			startHalfDay: WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) ? workingTimeRequest.startHalfDay : false,
			endHalfDay: WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest) ? workingTimeRequest.endHalfDay : false,
			breakTime: WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest) ? workingTimeRequest.breakTimeManual : 0
		};
		// the timezone-revealing behaviour only applies while duplicating, so the panel is rebuilt for this request
		this.initRangePanel(this.dialogMode === WorkingTimeRequestDialogMode.Duplicate);

		if (WorkingTimeRequestUtil.isWorkingTimeRequest(workingTimeRequest) && SecurityStatus.isCustomFieldsForRecordAllowed()) {
			updateCustomFields(this.dom, workingTimeRequest, this.config.customFieldDefinitions, this.customFieldComboboxes, this.hasCustomFieldCheckboxChangeListener);
		}
		this.toggleStatusView(workingTimeRequest);
		this.onSelectedWorkingTimeTypeChanged();
		this.proofAttachmentGallery?.update(this.workingTimeRequest.proofRequirement);
		this.substituteValidation?.setStartEndDate(startDate, endDate);

		this.dialog.show();
	}

	private toggleStatusView(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		const elements = this.dom.find("[data-hide-if-duplicate]");
		if (this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate) {
			this.dom.find("#workingTimeRequestDialogStatus").removeClass().addClass(`label request-status-${workingTimeRequest.status} request-category-${workingTimeRequest.workingTimeType.category}`)
				.children("span").text(messages["workingTimeRequestStatus." + workingTimeRequest.status]);
			this.dom.find(".status-comment").toggle(!!workingTimeRequest.statusComment)
				.find("#workingTimeRequestDialogStatusComment").text(workingTimeRequest.statusComment);
			this.dom.find(".request-approval-steps").toggle(!!workingTimeRequest.approvalSteps);
			this.dom.find(".request-cancellation-steps").toggle(!!workingTimeRequest.cancellationSteps);

			this.approvalWorkingTimeRequestStepsComponent.steps = workingTimeRequest.approvalSteps ?? [];
			this.cancellationWorkingTimeRequestStepsComponent.steps = workingTimeRequest.cancellationSteps ?? [];

			this.dom.find("[data-show-for-proof-status]").toggle(!!workingTimeRequest.proofStatus);
			if (workingTimeRequest.proofStatus) {
				this.dom.find("#workingTimeRequestDialogProofStatus").removeClass().addClass(`label proof-status status-${workingTimeRequest.proofStatus}`)
					.children("span").text(messages["proofStatus." + workingTimeRequest.proofStatus]);
			}

			elements.show();
		} else {
			// Don't show status elements in duplicate mode to be consistent with the create dialog
			elements.hide();
		}
	}

	private toggleWorkingTimeCollapse() {
		const showWorkingTimeCollapse = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			(this.workingTimeRequestStatus === WorkingTimeRequestStatus.Approved ||
				WorkingTimeRequestUtil.isInApprovalProcess(this.workingTimeRequest));

		const showStatusDescriptionRow = this.dialogMode === WorkingTimeRequestDialogMode.Edit ||
			this.dialogMode === WorkingTimeRequestDialogMode.Show && this.workingTimeRequestStatus === WorkingTimeRequestStatus.Approved;

		this.dom.find("#workingTimeRequestRevisionTableCollapse").collapse(showWorkingTimeCollapse ? "hide" : "show");
		this.dom.find("#workingTimeRequestDialogChangeDescriptionRow").toggle(showStatusDescriptionRow);
		this.dom.find("#workingTimeRequestHistoryRow").toggle(this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate);
	}

	private disableFields(disabled: boolean, overrideSelectorsDisabled?: string[]) {
		// the panel's fields are React-controlled, so they are disabled via the panel update below
		const inputFields = this.dom.find("input, textarea, select, button")
			.not("#workingTimeRequestDialogRangePanel *");
		inputFields.prop("disabled", disabled);
		if (overrideSelectorsDisabled) {
			this.dom.find(overrideSelectorsDisabled.join()).prop("disabled", true);
		}
		// The requested period follows only the dialog mode (edit vs. show). The "working time date/time changeable"
		// permission governs direct record edits, not requests, so it must not disable a request's period.
		this.rangeFieldsDisabled = disabled;
		this.updateRangePanel();
	}

	/**
	 * Offers the working time types recorded by request, plus the working time type of the given existing request.
	 * A notice request keeps its own working time type, which usually is not recorded by request, so the select then
	 * only shows that type.
	 */
	private initWorkingTimeTypeSelect(currentWorkingTimeType?: PartialWorkingTimeType) {
		const workingTimeTypeSelect = $("#workingTimeRequestDialogWorkingTimeType");
		workingTimeTypeSelect.empty();

		if (this.isNoticeRequest()) {
			const workingTimeType = this.workingTimeRequest.workingTimeType;
			workingTimeTypeSelect.append($("<option></option>", {value: workingTimeType.id}).text(workingTimeType.name));
			return;
		}

		const workingTimeTypesByRequest: PartialWorkingTimeType[] = this.config.workingTimeTypes.filter(wtt => {
			return wtt.id && wtt.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST;
		});
		// The type of an existing request may not be recorded by request. It still needs an option, as the select shows
		// it and submits it as workingTimeType.id when the request is saved.
		if (currentWorkingTimeType && !workingTimeTypesByRequest.some(wtt => wtt.id === currentWorkingTimeType.id)) {
			workingTimeTypesByRequest.push(currentWorkingTimeType);
		}
		workingTimeTypesByRequest.forEach(wtt => {
			workingTimeTypeSelect.append($("<option></option>", {value: wtt.id}).text(wtt.name));
		});
	}

	protected updateBorder() {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.removeClass("border-NOT_INITIALIZED");

		Object.keys(WorkingTimeTypeCategory)
			.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				modalContent.removeClass("border-" + c);
			});

		if (this.getSelectedWorkingTimeType() !== null) {
			modalContent.addClass("border-" + this.getSelectedWorkingTimeType().category);
		}
	}

	private updateActionButtonVisibility(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, showUserSelection: boolean = false) {
		const saveAndApproveImmediatelyButtonVisible = this.dialogMode === WorkingTimeRequestDialogMode.Duplicate &&
			permissionForSaveAndApproveButton(showUserSelection, this.userCombobox.getSelectedItem().id,
				this.config.isGroupLeaderForThemself);
		const requestButtonVisible = this.dialogMode === WorkingTimeRequestDialogMode.Duplicate;
		const saveButtonVisible = this.isSaveButtonVisible();
		const deleteButtonVisible = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest);
		const requestCancellationButtonVisible = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			this.workingTimeRequestStatus === WorkingTimeRequestStatus.Approved && workingTimeRequest.status === WorkingTimeRequestStatus.Approved &&
			workingTimeRequest.user.id === globals.userId;
		const cancelImmediatelyButton = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			this.workingTimeRequestStatus === WorkingTimeRequestStatus.Approved && workingTimeRequest.status === WorkingTimeRequestStatus.Approved && WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself);
		const immediatelyActionButtonsVisible = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) &&
			WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself);
		const immediatelyCancellationActionButtonsVisible = this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest) &&
			WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself);
		this.toggleEachStatusButtonVisibility(
			saveButtonVisible,
			deleteButtonVisible,
			requestCancellationButtonVisible,
			cancelImmediatelyButton,
			saveAndApproveImmediatelyButtonVisible,
			requestButtonVisible,
			immediatelyActionButtonsVisible,
			immediatelyCancellationActionButtonsVisible
		);
	}

	private isSaveButtonVisible() {
		return this.dialogMode !== WorkingTimeRequestDialogMode.Show &&
			this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate;
	}

	private toggleEachStatusButtonVisibility(saveButton: boolean,
	                                         deleteButton: boolean,
	                                         requestCancellationButton: boolean,
	                                         cancelImmediatelyButton: boolean,
	                                         saveAndApproveImmediatelyButton: boolean,
	                                         requestButton: boolean,
	                                         immediatelyActionButtonsVisible: boolean,
	                                         immediatelyCancellationActionButtonsVisible: boolean) {
		this.dialog.toggleButton("saveButton", saveButton);
		this.dialog.toggleButton("deleteButton", deleteButton);
		this.dialog.toggleButton("requestCancellationButton", requestCancellationButton);
		this.dialog.toggleButton("cancelImmediatelyButton", cancelImmediatelyButton);
		this.dialog.toggleButton("saveAndApproveImmediatelyButton", saveAndApproveImmediatelyButton);
		this.dialog.toggleButton("requestButton", requestButton);
		this.dialog.toggleButton("approveImmediatelyButton", immediatelyActionButtonsVisible);
		this.dialog.toggleButton("rejectImmediatelyButton", immediatelyActionButtonsVisible);
		this.dialog.toggleButton("approveCancellationImmediatelyButton", immediatelyCancellationActionButtonsVisible);
		this.dialog.toggleButton("rejectCancellationImmediatelyButton", immediatelyCancellationActionButtonsVisible);
		this.dialog.toggleButton("saveProofButton", false);
	}

	/**
	 * Whether an existing notice request is shown or edited. Its working time type cannot be changed, and it always
	 * covers whole days.
	 */
	private isNoticeRequest(): boolean {
		return this.dialogMode !== WorkingTimeRequestDialogMode.Duplicate &&
			!!this.workingTimeRequest && WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(this.workingTimeRequest);
	}

	private getSelectedWorkingTimeType(): PartialWorkingTimeType {
		if (this.isNoticeRequest()) {
			return this.workingTimeRequest.workingTimeType;
		}
		// the configured working time types do not necessarily contain the type of an existing request
		return this.getWorkingTimeTypeById(parseInt($("#workingTimeRequestDialogWorkingTimeType").val() as string, 10)) ??
			this.workingTimeRequest?.workingTimeType ?? null;
	}

	private getWorkingTimeTypeById(id: number): WorkingTimeType {
		const foundWorkingTimeTypes = $.grep(this.config.workingTimeTypes, (workingTimeType: WorkingTimeType) => {
			return workingTimeType.id === id;
		});
		return foundWorkingTimeTypes.length > 0 ? foundWorkingTimeTypes[0] : null;
	}

	private updateVacationInformation() {
		const dialog = $(this.config.dialogId);
		dialog.find("#workingTimeRequestDialogVacationRequired").text("");
		dialog.find("#workingTimeRequestDialogVacationLeft").text("");
		dialog.find("#workingTimeRequestDialogVacationLeft").closest(".col-xs-5").toggle(this.dialogMode !== WorkingTimeRequestDialogMode.Show);
		dialog.find(".vacationUnitLabel").text("");
		const workingTimeType = this.getSelectedWorkingTimeType();
		const startDate = this.rangeValue?.start ? getISODate(this.rangeValue.start) : null;
		const endDate = this.rangeValue?.end ? getISODate(this.rangeValue.end) : null;
		const workingTimeRequestId = this.dom.find(".id").val();
		const userId = dialog.find(".userId").val();
		if (this.isVacationInformationShown(workingTimeType) && startDate && endDate) {
			dialog.find(".workingTimeTypeCategory-vacation .loadingIndicator").show();
			this.updateVacationInformationRequest?.abort();
			this.updateVacationInformationRequest = api("recording/calculateVacation.json")
				.get({
					userId: userId ?? globals.userId,
					startDate: startDate,
					endDate: endDate,
					startHalfDay: this.rangeValue?.startHalfDay ?? false,
					endHalfDay: this.rangeValue?.endHalfDay ?? false,
					timeZone: getCurrentISOOffset(),
					workingTimeRequestId: workingTimeRequestId
				})
				.done((data: CalculateVacationResponse) => {
					FormUtil.removeAllFeedback($("#workingTimeRequestDialogForm"));
					this.updateRangePanel();
					dialog.find("#workingTimeRequestDialogVacationRequired").text(formatVacation(data.vacationRequired, data.vacationUnit));
					dialog.find("#workingTimeRequestDialogVacationLeft").text(formatVacation(data.vacationLeft, data.vacationUnit));
					dialog.find(".vacationUnitLabel").text(messages["vacationUnit." + data.vacationUnit]);
					dialog.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
					dialog.find("#workingTimeRequestDialogVacationWarning").text(data.warningText).toggle(!!data.warningText);
				})
				.fail((response: ErrorResponse) => {
					dialog.find("#workingTimeRequestDialogVacationRequired").text("?");
					dialog.find("#workingTimeRequestDialogVacationLeft").text("?");
					dialog.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
					dialog.find("#workingTimeRequestDialogVacationWarning").text(null).hide();
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#workingTimeRequestDialogForm"), response.responseJSON.fieldErrors);
						this.updateRangePanel(response.responseJSON.fieldErrors);
					}
				});
		} else {
			dialog.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
		}
	}

	/** Notices do not consume vacation, so notice requests show no vacation information. */
	private isVacationInformationShown(workingTimeType: PartialWorkingTimeType | null): boolean {
		return SecurityStatus.isTimeAccountAllowed() && !this.isNoticeRequest() &&
			workingTimeType?.category === WorkingTimeTypeCategory.Vacation;
	}

	private onSelectedWorkingTimeTypeChanged() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		// Notice requests always cover whole days, whatever the edit unit of their working time type
		const editUnit = workingTimeType != null && !this.isNoticeRequest() ? workingTimeType.editUnit : DurationUnit.Days;
		if (this.editUnit !== editUnit) {
			this.editUnit = editUnit;
			// a new value object re-seeds the panel, discarding transient edit state left over from the previous edit unit
			if (this.rangeValue) {
				this.rangeValue = {...this.rangeValue};
			}
		}
		this.updateRangePanel();

		this.dom.find(".workingTimeTypeCategory-vacation").toggle(this.isVacationInformationShown(workingTimeType));
		this.dom.find("#workingTimeRequestDialogCustomFields").toggle(editUnit === DurationUnit.Minutes && workingTimeType?.category === WorkingTimeTypeCategory.AttendanceTime);
		this.descriptionTypeahead.setFilter(workingTimeType ? {workingTimeTypeId: workingTimeType.id} : {});

		this.updateVacationInformation();
		this.updateBorder();
		this.updateTitle();
		this.substituteSelection?.setWorkingTimeType(workingTimeType);
		this.substituteValidation?.setWorkingTimeType(workingTimeType);
	}

	private updateTitle() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		const titleMessage = this.isNoticeRequest()
			? messages[this.dialogMode === WorkingTimeRequestDialogMode.Show
				? "workingTimeNotice.dialog.title.showRequest"
				: "workingTimeNotice.dialog.title.editRequest"]
			: messages.withArguments("requests.workingTimeRequestsUser.dialog." + this.dialogMode, workingTimeType.name);
		this.dialog.setTitle(titleMessage);
		this.dialog.setTitleLabel(messages["workingTimeRequestStatus." + this.workingTimeRequestStatus], "label-" + this.workingTimeRequestStatus);
	}

	private requestCancellation() {
		WorkingTimeRequestUtil.requestCancellationWorkingTimeRequest(
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestStatusChanged();
				this.closeDialog();
			},
			() => this.dialog.resetLoadingButtons()
		);
	}

	private _delete() {
		WorkingTimeRequestUtil.deleteWorkingTimeRequest(
			this.workingTimeRequest,
			() => {
				this.onWorkingTimeRequestDeleted();
				this.closeDialog();
			},
			() => this.dialog.resetLoadingButtons()
		);
	}

	private checkConfigurationAndSave(modal: BootstrapModal.Modal, approveImmediately: boolean = false): void {
		if (this.substituteValidation?.hasAlreadySubstitutions()) {
			BootstrapModal.confirm({
				title: "workingTimeRequestDialog.alreadySubstitute.title",
				message: messages.withArguments("workingTimeRequestDialog.alreadySubstitute.info", this.substituteValidation?.getAlreadySubstitutionsUserNames()),
				escapeText: false,
				preventDoubleClickButtons: true,
				yesAction: (modal: BootstrapModal.Modal) => {
					this.save(approveImmediately);
					modal.close();
				},
				noAction: (m: BootstrapModal.Modal) => {
					m.close();
					modal.resetLoadingButtons();
				}
			});
		} else {
			this.save(approveImmediately);
		}
	}

	private save(approveImmediately: boolean = false) {
		// The subtype is known from the request or the selected working time type's edit unit, so we post to the matching endpoint.
		let saveRequest: Promise<unknown>;
		if (this.isNoticeRequest()) {
			saveRequest = this.saveWorkingTimeNoticeRequest();
		} else if (this.editUnit === DurationUnit.Minutes) {
			saveRequest = WorkingTimeRequestUtil.saveWorkingTimeRequestWithProofAttachments(this.getParamsForSave(approveImmediately), this.proofAttachmentGallery, (workingTimeRequest) => this.edit(workingTimeRequest));
		} else {
			saveRequest = WorkingTimeRequestUtil.saveWorkingTimeDateSpanRequestWithProofAttachments(this.getParamsForSave(approveImmediately), this.proofAttachmentGallery, (workingTimeRequest) => this.edit(workingTimeRequest));
		}
		saveRequest
			.then(
				() => {
					this.onWorkingTimeRequestEdited();
					statusElementsHolder.updateRequestsApproval();
					this.closeDialog();
				}
			)
			.catch((error: ErrorResponse) => {
				const fieldErrors = error?.responseJSON?.fieldErrors;
				if (fieldErrors) {
					FormUtil.markFieldErrors(this.dom.find("#workingTimeRequestDialogForm"), fieldErrors);
					this.updateRangePanel(fieldErrors);
				}
			})
			.finally(() => this.dialog.resetLoadingButtons());
	}

	/**
	 * Saves an edited notice request through the notice endpoint, which takes the inclusive first and last day.
	 * Notice requests have no proofs, and new ones are created in the notice dialog.
	 */
	private async saveWorkingTimeNoticeRequest(): Promise<void> {
		await api("/notices/saveWorkingTimeNoticeRequest.json").postForm(this.dom.find("#workingTimeRequestDialogForm"), undefined, [
			{name: "workingTimeTypeId", value: String(this.workingTimeRequest.workingTimeType.id)},
			{name: "start", value: getISODate(this.rangeValue.start)},
			{name: "end", value: getISODate(this.rangeValue.end)}
		]);
	}

	/**
	 * Saves only the proof attachments, not the working time request itself.
	 * Used for approved requests where changing the request is not allowed,
	 * but changing attachments is still possible for admins and possibly team leaders.
	 * @private
	 */
	private saveProofAttachmentsOnly() {
		WorkingTimeRequestUtil.saveProofAttachments(this.getParamsForSave(), this.proofAttachmentGallery, (workingTimeRequest) => this.edit(workingTimeRequest))
			.then(
				() => {
					this.onWorkingTimeRequestEdited();
					statusElementsHolder.updateRequestsApproval();
					this.closeDialog();
				},
				// save errors are already surfaced by api(); swallow the rejection so it does not bubble as an unhandled promise rejection
				() => { /* handled in api() */ }
			)
			.finally(() => this.dialog.resetLoadingButtons());
	}

	private saveProofsBeforeAction(): Promise<SaveWorkingTimeRequestResponse> {
		if (!this.hasPendingProofAttachmentChanges()) {
			return Promise.resolve({} as SaveWorkingTimeRequestResponse);
		}

		return WorkingTimeRequestUtil.saveProofAttachments(this.getParamsForSave(), this.proofAttachmentGallery);
	}

	private hasPendingProofAttachmentChanges() {
		const filesToUpload = this.proofAttachmentGallery?.getFilesToUpload() ?? [];
		const attachmentsToDelete = this.proofAttachmentGallery?.getAttachmentsToDelete() ?? [];
		return filesToUpload.length > 0 || attachmentsToDelete.length > 0;
	}

	private getParamsForSave(approveImmediately: boolean = false) {
		if (this.getSelectedWorkingTimeType()?.editUnit !== DurationUnit.Minutes || this.getSelectedWorkingTimeType()?.category !== WorkingTimeTypeCategory.AttendanceTime) {
			removeCustomFieldValues(this.dom);
		}

		const form = this.dom.find("#workingTimeRequestDialogForm");

		// serialize form and also include disabled fields (necessary for workingTimeTypeId);
		const data = form.serializeArray().concat(FormUtil.serializeArrayDisabled(form)).concat([
			{name: "start", value: getISOZonedDateTime(this.rangeValue.start)},
			{name: "end", value: getISOZonedDateTime(this.rangeValue.end)},
			{name: "startHalfDay", value: String(this.rangeValue.startHalfDay)},
			{name: "endHalfDay", value: String(this.rangeValue.endHalfDay)},
			{name: "breakTime", value: String(this.rangeValue.breakTime)}
		]);
		if (approveImmediately) {
			data.push({name: "status", value: WorkingTimeRequestStatus.Approved});
		}
		return data;
	}

	private initUserCombobox() {
		const userIdElement = this.dom.find(".userId");
		if (userIdElement.length > 0) {
			this.userCombobox = new Combobox<PartialUser>({
				items: [],
				formatter: user => user.fullname,
				idElement: userIdElement,
				filterElement: this.dom.find(".userFilter"),
				caretElement: this.dom.find(".userCaret"),
				clearable: false
			});
			$(this.userCombobox).on(Combobox.EVENT_CHANGED, (_, user: User) => {
				this.substituteSelection?.setUser(user?.id);
			});
		}
	}

	private initSubstitutes() {
		this.substituteSelection = document.getElementById("workingTimeRequestDialogSubstituteSelection") as SubstituteSelection;
		this.substituteValidation = document.getElementById("workingTimeRequestDialogSubstituteValidation") as SubstituteValidation;
		this.substituteSelection?.init();
		this.substituteValidation?.init(false);

		this.initSubstitutesListeners();
	}

	private initSubstitutesListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTED, e => {
			const customEvent = e as CustomEvent<User>;
			this.substituteValidation?.validateSubstitute(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_USER_SELECTED, e => {
			const customEvent = e as CustomEvent<number>;
			this.substituteValidation?.validateUser(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTION_VISIBILITY_TOGGLED, e => {
			const customEvent = e as CustomEvent<boolean>;
			this.substituteValidation?.setRequiresSubstitute(customEvent.detail);
		});
	}

	private initButtonLoadingListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteValidation?.addEventListener(SubstituteSelection.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
		this.substituteValidation?.addEventListener(SubstituteSelection.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
	}

	private updateLoadingButtons(modifyCount: number) {
		this.currentRequestCount += modifyCount;
		if (this.currentRequestCount > 0) {
			const buttons = this.dialog.getButtons("btn-primary");
			Button.setLoading(buttons);
		} else {
			this.currentRequestCount = 0;
			this.dialog.resetLoadingButtons();
		}
	}
}
